import React, { useRef, useEffect, useMemo, useState } from "react";
import * as d3 from "d3";

interface DataPoint {
    label: string;
    value: number;
}

interface BarChartProps {
    data: DataPoint[];
    numOfLines?: number;
    initialWidth?: number;
    height?: any;
    line?: boolean;
    chartBarWidth?: number;
    yAxisLabelType?: 'inPercent' | 'inValue' | 'custom';
    customLabelFormatter?: (value: number) => string;
    currency: string
}

const formatNumber = (num: number, symbol: string, labelType: string) => {
    if (labelType === 'inPercent') {
        return `${d3.format(".0%")(num / 100)}`;
    } else if (labelType === 'inValue') {
        return `${symbol || ''} ${d3.format(".2s")(num).replace('k', 'K')}`;
    }
    return num.toString();
};

function wrapText(textSelection: any, width: number) {
    textSelection.each(function (this: any) {
      const text = d3.select(this);
      const originalText = text.text();
      
      if (originalText.length <= 8) {
        return;
      }
      
      if (originalText.length > 12) {
        text.text(originalText.substring(0, 10) + '...');
        return;
      }
      
      const words = originalText.split(/\s+/).reverse();
      let word;
      let line: string[] = [];
      let lineNumber = 0;
      const lineHeight = 1.1; // ems
      const y = text.attr("y");
      const dy = parseFloat(text.attr("dy")) || 0;
  
      let tspan = text
        .text(null)
        .append("tspan")
        .attr("x", 0)
        .attr("y", y)
        .attr("dy", `${dy}em`);
  
      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(" "));
        if (tspan.node() && tspan.node()!.getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(" "));
          line = [word];
          tspan = text
            .append("tspan")
            .attr("x", 0)
            .attr("y", y)
            .attr("dy", `${++lineNumber * lineHeight + dy}em`)
            .text(word);
        }
      }
    });
  }
  
const BarChart: React.FC<BarChartProps> = ({
    data,
    numOfLines = 5,
    initialWidth = 500,
    height = 400,
    line = false,
    yAxisLabelType = 'inValue',
    currency,
    customLabelFormatter,
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(initialWidth);

    const maxValue = useMemo(() => Math.max(...data.map((d) => d.value)), [data]);

    const yAxisMaxValue = useMemo(() => maxValue * 1.25, [maxValue]);

    const yAxisTicks = useMemo(() => {
        const ticks = [];
        const interval = yAxisMaxValue / numOfLines;
        for (let i = 0; i <= numOfLines; i++) {
            ticks.push(interval * i);
        }
        return ticks;
    }, [yAxisMaxValue, numOfLines]);


    const needsHorizontalScroll = data.length > 12;
    const minGroupWidth = 120; 
    const calculatedChartWidth = needsHorizontalScroll 
        ? Math.max(data.length * minGroupWidth, containerWidth)
        : containerWidth;

    // ResizeObserver to make the chart responsive

    useEffect(() => {
        const wrapper = wrapperRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0]) {
                const newWidth = entries[0].contentRect.width;
                setContainerWidth(newWidth);
            }
        });

        if (wrapper) {
            resizeObserver.observe(wrapper);
        }
    
        return () => {
            if (wrapper) {
                resizeObserver.unobserve(wrapper);
            }
        };
    }, []);
    useEffect(() => {
        if (containerWidth === 0) return;

        const svg = d3.select(svgRef.current);

        svg.selectAll("*").remove();

        // Calculate dynamic left margin based on label length (accounting for wrapping)
        // const sampleLabel = customLabelFormatter 
        //     ? customLabelFormatter(yAxisMaxValue)
        //     : formatNumber(yAxisMaxValue, currency, yAxisLabelType);
        const maxLabelWidth = 80; // Maximum width for wrapped labels
        const dynamicLeftMargin = Math.max(60, maxLabelWidth + 30);
        
        const margin = { top: 20, right: 20, bottom: 25, left: dynamicLeftMargin };
        const barWidth = calculatedChartWidth - margin.left - margin.right;
        const barHeight = maxValue ? height - margin.top - margin.bottom : 0;

        const x = d3
            .scaleBand()
            .domain(data.map((d) => d.label))
            .range([0, barWidth])
            .padding(0.5);

        const y = d3.scaleLinear().domain([0, yAxisMaxValue]).range([barHeight, 0]);

        
        const chart = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${maxValue?margin.top:height-50})`);
        
        if (line) {
            yAxisTicks.slice(1).forEach((tick) => {
                chart
                    .append("line")
                    .attr("class", "dotted-line")
                    .attr("x1", 0)
                    .attr("y1", y(tick))
                    .attr("x2", barWidth)
                    .attr("y2", y(tick))
                    .style("stroke", "#D3D3D4")
                    .style("stroke-dasharray", "3,3")
                    .style("opacity", 1);
            });
        }

        chart
            .selectAll(".bar")
            .data(data)
            .enter()
            .append("rect")
            .attr("rx", 5)
            .attr("ry", 5)
            .attr("class", "bar")
            .attr("x", (d: any) => x(d.label))
            .attr("y", (d: any) => y(d.value))
            .attr("width", x.bandwidth())
            .attr("height", (d: any) => barHeight - y(d.value))
            .style("fill", (d: any) => d.color);

        const xAxis = chart
            .append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${barHeight})`)
            .call(d3.axisTop(x).tickPadding(10) as any)
            .call(d3.axisBottom(x).tickPadding(5) as any)
            // .call(d3.axisLeft(x).tickPadding(5) as any)

        xAxis.selectAll(".tick line").remove();
        xAxis.selectAll(".tick text").call(wrapText,80);   

        // Calculate optimal number of ticks to prevent overlap
        const availableHeight = barHeight;
        const minTickSpacing = 30; // Minimum pixels between ticks
        const maxOptimalTicks = Math.floor(availableHeight / minTickSpacing);
        const optimalNumTicks = Math.min(numOfLines, maxOptimalTicks, 8);
        
        // Generate optimal tick values
        const optimalTicks = [];
        const optimalInterval = yAxisMaxValue / optimalNumTicks;
        for (let i = 0; i <= optimalNumTicks; i++) {
            optimalTicks.push(optimalInterval * i);
        }

        const yAxis = chart
            .append("g")
            .attr("class", "y-axis")
            .call(
                d3.axisLeft(y)
                    .tickValues(optimalTicks)
                    .tickFormat((d: any) =>
                        customLabelFormatter
                            ? customLabelFormatter(d)
                            : formatNumber(d, currency, yAxisLabelType)
                    )
                    .tickSizeOuter(0)
                    .tickPadding(8) as any, // Add padding between ticks and labels
            );

        yAxis.selectAll(".tick line").remove();
        yAxis.select(".domain").attr("stroke", "none");
        xAxis.select(".domain").attr("stroke", "none");
        
        // Style y-axis labels for better readability and apply text wrapping
        yAxis.selectAll(".tick text")
            .style("font-size", "11px")
            .style("fill", "#666")
            .style("font-family", "Inter, system-ui, Avenir, Helvetica, Arial, sans-serif")
            .style("text-anchor", "end")
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, containerWidth, height, line, yAxisMaxValue, yAxisTicks, currency, yAxisLabelType, customLabelFormatter, calculatedChartWidth]);

    return (
        <div ref={wrapperRef} 
        style={{ 
            width: '100%', 
            overflowX: needsHorizontalScroll ? 'auto' : 'hidden',
            overflowY: 'hidden'
        }}>
            <svg ref={svgRef} width={calculatedChartWidth} height={height} viewBox={`0 0 ${calculatedChartWidth} ${height}`} />
        </div>
    );
};

export { BarChart };
export default BarChart;
